/**
 * Hook that edits the dedicated contract end date of an extraction (SE-4881).
 *
 * Calls the `editContractEndDate` mutation which rewrites the
 * extraction's `endDate` column to a literal `YYYY-MM-DD` string and
 * returns the updated extraction. The backend flips a SAVED extraction
 * back to PENDING_REVIEW, is owner-gated, and only permits the edit
 * while the extraction is PENDING_REVIEW or SAVED.
 *
 * Because the mutation returns the full `ContractExtractionObject`
 * (`id` + `endDate` + `status`), Apollo normalises the response into
 * the cache by `id`, so the active `GetContractExtraction` observer the
 * drawer subscribes to re-renders with the new `endDate` automatically.
 * The contracts overview table (`PlayerContractsOverview`) keys its
 * `contractEndDate` on a different object type (`PlayerContractRow`,
 * keyed by `playerId`), so it is refetched explicitly here so the
 * restored "Contract End" column updates in lockstep.
 *
 * The GraphQL document is defined inline via `gql` (same convention as
 * the other extraction hooks in this feature) so it can ship before the
 * codegen step runs against the updated schema.
 * @module features/contracts/hooks/useEditContractEndDate
 */

import { gql, useMutation } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PlayerContractsOverviewDocument } from "@/generated/graphql";

import type { ContractExtraction } from "../types";

import { activeContractExtractionRefetch } from "./contractExtractionRefetch";

/** GraphQL document for the EditContractEndDate mutation. */
export const EDIT_CONTRACT_END_DATE_MUTATION = gql`
  mutation EditContractEndDate($input: EditContractEndDateInput!) {
    editContractEndDate(input: $input) {
      id
      playerId
      documentType
      status
      extractedData
      savedAt
      endDate
    }
  }
`;

/** Shape of the EditContractEndDate mutation response. */
interface EditContractEndDateResponse {
  readonly editContractEndDate: ContractExtraction | null;
}

/** Shape of the EditContractEndDate mutation variables. */
interface EditContractEndDateVariables {
  readonly input: {
    readonly extractionId: string;
    readonly endDate: string | null;
  };
}

/** Public API returned by `useEditContractEndDate`. */
export interface UseEditContractEndDateResult {
  /**
   * Edits the contract end date of an extraction. On success, Apollo
   * updates the cached extraction (the mutation returns the entity) and
   * the `PlayerContractsOverview` query is refetched so the overview's
   * "Contract End" column reflects the new value.
   * @param extractionId - UUID of the extraction to update
   * @param endDate - New end date as a literal `YYYY-MM-DD` string, or null to clear it
   * @param playerId - Player UUID (needed for the extraction query refetch variables)
   * @param documentType - Document type (needed for the extraction query refetch variables)
   * @returns `true` when the server returns the updated extraction, `false` otherwise
   */
  readonly editContractEndDate: (
    extractionId: string,
    endDate: string | null,
    playerId: string,
    documentType: string
  ) => Promise<boolean>;
  /** Whether an edit request is currently in flight. */
  readonly isEditing: boolean;
}

/**
 * Wraps the EditContractEndDate mutation with loading state, the active
 * extraction refetch, and an overview refetch on success.
 * @returns Imperative edit function + loading state
 */
export function useEditContractEndDate(): UseEditContractEndDateResult {
  const [runMutation] = useMutation<
    EditContractEndDateResponse,
    EditContractEndDateVariables
  >(EDIT_CONTRACT_END_DATE_MUTATION);

  const [isEditing, setIsEditing] = useState(false);

  const editContractEndDate = useCallback(
    async (
      extractionId: string,
      endDate: string | null,
      playerId: string,
      documentType: string
    ): Promise<boolean> => {
      setIsEditing(true);
      try {
        const { data } = await runMutation({
          variables: { input: { extractionId, endDate } },
          // The drawer's per-player extraction observer needs a fresh
          // read so the dedicated section re-renders against the new
          // server state; the overview refetch flips the restored
          // "Contract End" column on the table.
          refetchQueries: [
            activeContractExtractionRefetch(playerId, documentType),
            {
              query: PlayerContractsOverviewDocument,
              variables: { archivedOnly: false },
            },
          ],
          awaitRefetchQueries: true,
        });
        return data?.editContractEndDate != null;
      } catch {
        return false;
      } finally {
        setIsEditing(false);
      }
    },
    [runMutation]
  );

  return useMemo(
    () => ({ editContractEndDate, isEditing }),
    [editContractEndDate, isEditing]
  );
}
